/**
 * A confirmation for a consequential operator action (activate on air,
 * deactivate, reset, end a production): what will happen, one confirm button
 * in the action's colour, and cancel. The action itself is the container's
 * (`onConfirm`); while it is pending the dialog stays open and says so.
 */
import { Button } from '../ui/button.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';

export type ConfirmDialogProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly description: string;
  readonly confirmLabel: string;
  /** `take`: puts something on air; `destructive`: stops or discards something. */
  readonly tone: 'take' | 'destructive' | 'default';
  readonly pending: boolean;
  readonly onConfirm: () => void;
};

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, tone, pending, onConfirm }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" variant={tone} onClick={onConfirm} disabled={pending} data-testid="confirm-action" autoFocus>
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
